/**
 * DelegationModal - Send connection request to accountant/bookkeeper
 * v2.26 - 2026-01-30 - Initial implementation
 *
 * Allows user to delegate data source connection to another person.
 * Generates a magic link that the delegate can use to complete OAuth.
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Paper,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';

interface DelegationModalProps {
  open: boolean;
  onClose: () => void;
  connectorType: string;
  connectorDisplayName: string;
  onSendRequest: (email: string, message?: string) => Promise<{ success: boolean; error?: string }>;
}

const DelegationModal: React.FC<DelegationModalProps> = ({
  open,
  onClose,
  connectorType,
  connectorDisplayName,
  onSendRequest,
}) => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSend = async () => {
    // Validate email
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await onSendRequest(email.trim(), message.trim() || undefined);
      if (result.success) {
        setSent(true);
      } else {
        setError(result.error || 'Failed to send request');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send request');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setEmail('');
      setMessage('');
      setError(null);
      setSent(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" component="span" sx={{ fontWeight: 600 }}>
          {sent ? 'Request Sent!' : `Delegate ${connectorDisplayName} Connection`}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {sent ? (
          // Success state
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              Connection request sent!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              We've sent an email to <strong>{email}</strong> with instructions to connect {connectorDisplayName}.
            </Typography>

            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                What happens next?
              </Typography>
              <Stepper orientation="vertical" sx={{ mt: 2 }}>
                <Step active>
                  <StepLabel>
                    <Typography variant="body2">Your delegate receives the email</Typography>
                  </StepLabel>
                </Step>
                <Step>
                  <StepLabel>
                    <Typography variant="body2">They click the link and authorize access</Typography>
                  </StepLabel>
                </Step>
                <Step>
                  <StepLabel>
                    <Typography variant="body2">{connectorDisplayName} is connected to your account</Typography>
                  </StepLabel>
                </Step>
              </Stepper>
            </Paper>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              The link expires in 7 days.
            </Typography>
          </Box>
        ) : (
          // Form state
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enter the email of your accountant, bookkeeper, or IT administrator who has access to your {connectorDisplayName} account.
              They'll receive a secure link to complete the connection.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Delegate's Email"
                type="email"
                required
                placeholder="accountant@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError(null);
                }}
                disabled={loading}
                InputProps={{
                  startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  sx: { borderRadius: 1 }
                }}
              />

              <TextField
                fullWidth
                label="Message (optional)"
                multiline
                rows={3}
                placeholder="Hi, please connect our QuickBooks account to MyLojic..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={loading}
                InputProps={{
                  sx: { borderRadius: 1 }
                }}
              />
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                The delegate will only be able to connect {connectorDisplayName} to your account.
                They won't have access to your MyLojic dashboard.
              </Typography>
            </Alert>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {sent ? (
          <Button
            variant="contained"
            onClick={handleClose}
            sx={{ bgcolor: 'primary.main' }}
          >
            Done
          </Button>
        ) : (
          <>
            <Button
              onClick={handleClose}
              disabled={loading}
              sx={{ color: 'text.secondary' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSend}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              sx={{
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' },
                minWidth: 140,
              }}
            >
              {loading ? 'Sending...' : 'Send Request'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DelegationModal;
